import { useEffect, useMemo, useState } from "react";
import { type HeroMarketplaceFilter, type HeroSlide } from "@/hooks/useHeroSlides";

interface HeroRouteOption {
  label: string;
  path: string;
}

interface Props {
  slide?: HeroSlide | null;
  routeOptions: HeroRouteOption[];
  marketplaceOptions: string[];
  onSave: (data: Omit<HeroSlide, "id">) => void;
  onClose: () => void;
}

export default function HeroSlideModal({
  slide,
  routeOptions,
  marketplaceOptions,
  onSave,
  onClose,
}: Props) {
  const [imageUrl, setImageUrl] = useState("");
  const [categoryPath, setCategoryPath] = useState(routeOptions[0]?.path ?? "/categorias");
  const [marketplace, setMarketplace] = useState<HeroMarketplaceFilter>("Todos");

  useEffect(() => {
    if (slide) {
      setImageUrl(slide.imageUrl);
      setCategoryPath(slide.categoryPath);
      setMarketplace(slide.marketplace);
      return;
    }

    setImageUrl("");
    setCategoryPath(routeOptions[0]?.path ?? "/categorias");
    setMarketplace("Todos");
  }, [routeOptions, slide]);

  useEffect(() => {
    if (!marketplaceOptions.includes(marketplace)) {
      setMarketplace("Todos");
    }
  }, [marketplace, marketplaceOptions]);

  const selectedRoute = useMemo(
    () => routeOptions.find((option) => option.path === categoryPath) ?? routeOptions[0],
    [categoryPath, routeOptions]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      imageUrl,
      categoryPath,
      categoryLabel: selectedRoute?.label ?? "Categorias",
      marketplace,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {slide ? "Editar slide do Hero" : "Novo slide do Hero"}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">URL da imagem *</label>
            <input
              required
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
            {imageUrl && (
              <div className="mt-3 rounded-2xl overflow-hidden border border-gray-200 bg-gray-50">
                <img src={imageUrl} alt="Prévia do slide" className="w-full h-44 object-cover object-center" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Link da categoria *</label>
              <select
                value={categoryPath}
                onChange={(e) => setCategoryPath(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer"
              >
                {routeOptions.map((option) => (
                  <option key={option.path} value={option.path}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Marketplace</label>
              <select
                value={marketplace}
                onChange={(e) => setMarketplace(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer"
              >
                {marketplaceOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-700">
            <p className="font-semibold">Destino do slide</p>
            <p className="mt-1">{selectedRoute?.label ?? "Categorias"}</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {slide ? "Salvar slide" : "Adicionar slide"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
